// Just enough of CSP Level 3 source-list matching to answer one question:
// would our own frame-ancestors directive(s) allow these embedding origins?
// See https://www.w3.org/TR/CSP3/#match-url-to-source-expression

const DEFAULT_PORTS = { 'http:': '80', 'https:': '443' };

const parseUrl = value => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

const portOf = url => url.port || DEFAULT_PORTS[url.protocol] || '';

// A source scheme matches the URL's scheme, or is http: and the URL https:.
const schemeMatches = (sourceScheme, urlScheme) =>
  sourceScheme === urlScheme ||
  (sourceScheme === 'http:' && urlScheme === 'https:');

const matchesHostSource = (expression, url, self) => {
  const match = expression.match(
    /^(?:([a-z][a-z0-9+.-]*):\/\/)?(\*|(?:\*\.)?[^:/]+)(?::(\*|\d+))?(\/.*)?$/i,
  );
  if (!match) return false;
  const [, scheme, host, port] = match;

  if (scheme) {
    if (!schemeMatches(`${scheme.toLowerCase()}:`, url.protocol)) return false;
  } else if (!schemeMatches(self.protocol, url.protocol)) {
    return false;
  }

  const sourceHost = host.toLowerCase();
  if (sourceHost.startsWith('*.')) {
    if (!url.hostname.endsWith(sourceHost.slice(1))) return false;
  } else if (sourceHost !== '*' && sourceHost !== url.hostname) {
    return false;
  }

  if (port === '*') return true;
  // No port in the source means the default port of the URL's scheme.
  if (port === undefined) return url.port === '';
  // :80 also matches :443 when http has been upgraded to https.
  return (
    port === portOf(url) ||
    (port === '80' && url.protocol === 'https:' && portOf(url) === '443')
  );
};

const matchesExpression = (expression, url, self) => {
  const lower = expression.toLowerCase();
  if (lower === "'self'") {
    return (
      url.origin === self.origin ||
      (self.protocol === 'http:' &&
        url.protocol === 'https:' &&
        url.host === self.host)
    );
  }
  if (lower.startsWith("'")) return false; // other keywords don't apply here
  if (lower === '*')
    return url.protocol === 'http:' || url.protocol === 'https:';
  if (/^[a-z][a-z0-9+.-]*:$/.test(lower))
    return schemeMatches(lower, url.protocol);
  return matchesHostSource(expression, url, self);
};

const matchesSourceList = (sourceList, origin, self) => {
  const url = parseUrl(origin);
  if (!url) return false;
  // 'none' only means anything when it is the whole list.
  return sourceList.some(expression =>
    matchesExpression(expression, url, self),
  );
};

/**
 * Returns the source list of each frame-ancestors directive in the given
 * header values. A value can hold several comma-separated policies; within a
 * policy only the first occurrence of a directive counts.
 */
export const frameAncestorsSourceLists = headerValues =>
  headerValues
    .filter(Boolean)
    .flatMap(value => value.split(','))
    .map(policy => {
      const directive = policy
        .split(';')
        .map(part => part.trim().split(/\s+/))
        .find(([name]) => name?.toLowerCase() === 'frame-ancestors');
      return directive?.slice(1);
    })
    .filter(Boolean);

/**
 * True when there is at least one frame-ancestors directive, and every one of
 * them allows every ancestor origin. Multiple policies are each enforced (or
 * reported) independently, so an ancestor has to pass all of them.
 */
export const isFramingAllowed = (headerValues, ancestorOrigins, selfOrigin) => {
  const sourceLists = frameAncestorsSourceLists(headerValues);
  const self = parseUrl(selfOrigin);
  if (!sourceLists.length || !ancestorOrigins.length || !self) return false;
  return sourceLists.every(sourceList =>
    ancestorOrigins.every(origin =>
      matchesSourceList(sourceList, origin, self),
    ),
  );
};
